import {useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';

export const PURCHASE_PARAM = 'purchase';
/** Legacy `?asset=<assetId>` param, resolved to the purchase holding that asset. */
export const LEGACY_ASSET_PARAM = 'asset';

interface PurchaseRef {
  id: string;
  assets: {id: string}[];
}

interface UseBuyerPurchaseParamResult {
  selectedIndex: number;
  setSelectedPurchase: (index: number) => void;
}

/** Syncs the selected purchase tab with ?purchase=<purchaseId>, falling back to index 0 when absent or unmatched. */
export function useBuyerPurchaseParam(purchases: PurchaseRef[]): UseBuyerPurchaseParamResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedIndex = findSelectedPurchaseIndex(purchases, searchParams);

  const setSelectedPurchase = useCallback(
    (index: number) => {
      const purchase = purchases[index];
      if (!purchase) return;
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete(LEGACY_ASSET_PARAM);
          next.set(PURCHASE_PARAM, purchase.id);
          return next;
        },
        {replace: true}
      );
    },
    [purchases, setSearchParams]
  );

  return {selectedIndex, setSelectedPurchase};
}

/** ?purchase wins; a legacy ?asset link selects the purchase holding that asset. Index 0 when neither matches. */
export function findSelectedPurchaseIndex(
  purchases: PurchaseRef[],
  searchParams: URLSearchParams
): number {
  const purchaseParam = searchParams.get(PURCHASE_PARAM);
  const legacyAssetParam = searchParams.get(LEGACY_ASSET_PARAM);
  const index = purchases.findIndex(purchase =>
    purchaseParam
      ? purchase.id === purchaseParam
      : purchase.assets.some(asset => asset.id === legacyAssetParam)
  );
  return Math.max(index, 0);
}
